import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  BaseEffect,
  BtPayPaymentInformationActions,
  GetPaymentAcceptanceRulesAction,
  GetPaymentRemittanceRulesAction,
  PayOnAccountCoreComponentActions,
  selectIsBuyerOriginApp,
  selectIsCountryCanada,
  selectIsPaymentLinks,
  selectVirtualOtherwisePrimaryTenantId,
  SessionActions,
} from '@arc/unified-payments-frontend/shared-stores';
import { selectIsStaticPaymentLink } from '@arc/unified-payments-frontend/payment-links';
import { of } from 'rxjs';
import { catchError, exhaustMap, filter, map } from 'rxjs/operators';
import {
  LoadingSpinnerService,
  PaymentAcceptanceRulesServiceFactory,
  PaymentInformationServiceFactory,
  PreAuthorizedDebitModeV1Response,
} from '@arc/unified-payments-frontend/shared-payment';
import { selectCreatePayOnAccountPaymentPreviewRequestData } from '../../../store';
import {
  AppConfigSettings,
  UserNotificationAction,
} from '@arc/arc-shared-frontend';
import { TranslateService } from '@ngx-translate/core';
import { concatLatestFrom } from '@ngrx/operators';
import { HttpErrorResponse } from '@angular/common/http';

@Injectable()
export class PayOnAccountCoreComponentEffects extends BaseEffect {
  constructor(
    private store: Store,
    private paymentInformationServiceFactory: PaymentInformationServiceFactory,
    private paymentRulesServiceFactory: PaymentAcceptanceRulesServiceFactory,
    private translationService: TranslateService,
    private appConfig: AppConfigSettings,
    actions$: Actions,
    loadingSpinnerService: LoadingSpinnerService,
  ) {
    super(actions$, loadingSpinnerService);
  }

  loadSessionSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PayOnAccountCoreComponentActions.initializePayOnAccountCoreAction),
      exhaustMap((action) =>
        of(
          SessionActions.GetSessionSummaryAction({
            payload: {
              sessionId: action.sessionId,
            },
          }),
        ),
      ),
    ),
  );

  loadPaymentRules$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionActions.GetSessionSummarySuccessAction),
      exhaustMap(() => of(GetPaymentAcceptanceRulesAction())),
    ),
  );

  loadPaymentRemittanceRules$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionActions.GetSessionSummarySuccessAction),
      concatLatestFrom(() => [
        this.store.select(selectVirtualOtherwisePrimaryTenantId),
      ]),
      exhaustMap(([, tenantId]) =>
        of(
          GetPaymentRemittanceRulesAction({
            payload: tenantId,
          }),
        ),
      ),
    ),
  );

  loadPreAuthorizedDebitMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionActions.GetSessionSummarySuccessAction),
      concatLatestFrom(() => [
        this.store.select(selectIsCountryCanada),
        this.store.select(selectVirtualOtherwisePrimaryTenantId),
      ]),
      filter(([, isCountryCanada]) => isCountryCanada),
      exhaustMap(([, , tenantId]) =>
        this.paymentRulesServiceFactory
          .create()
          .getPreAuthorizedDebitModeV1(tenantId)
          .pipe(
            map((response: PreAuthorizedDebitModeV1Response) => {
              return PayOnAccountCoreComponentActions.loadPreAuthorizedDebitModeSuccess(
                {
                  payload: response.preAuthorizedDebitMode,
                },
              );
            }),
            catchError((error: HttpErrorResponse) =>
              of(
                PayOnAccountCoreComponentActions.loadPreAuthorizedDebitModeError(
                  {
                    error,
                  },
                ),
              ),
            ),
          ),
      ),
    ),
  );

  createPaymentPreview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionActions.GetSessionSummarySuccessAction),
      concatLatestFrom(() => [
        this.store.select(selectIsStaticPaymentLink),
        this.store.select(selectCreatePayOnAccountPaymentPreviewRequestData),
      ]),
      exhaustMap(([, isStaticPaymentLink, requestData]) => {
        if (isStaticPaymentLink) {
          return of(
            PayOnAccountCoreComponentActions.skipPayOnAccountPaymentPreviewAction(),
          );
        }
        return this.paymentInformationServiceFactory
          .create()
          .updatePaymentPreviewPayOnAccountV1(
            requestData.tenantId,
            requestData.paymentPreviewRequest,
          )
          .pipe(
            map((payload) =>
              PayOnAccountCoreComponentActions.createPayOnAccountPaymentPreviewCompleteAction(
                {
                  payload,
                },
              ),
            ),
            catchError(() =>
              of(
                PayOnAccountCoreComponentActions.createPayOnAccountPaymentPreviewErrorAction(),
              ),
            ),
          );
      }),
    ),
  );

  createPaymentPreviewError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        PayOnAccountCoreComponentActions
          .createPayOnAccountPaymentPreviewErrorAction.type,
      ),
      map(() => {
        return new UserNotificationAction({
          type: 'error',
          message: this.translationService.instant(
            'ARC_PAYMENT_INTL_GENERIC_ERROR',
          ) as string,
        });
      }),
    ),
  );

  loadBuyerAccounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionActions.GetSessionSummarySuccessAction),
      concatLatestFrom(() => [
        this.store.select(selectIsBuyerOriginApp),
        this.store.select(selectIsPaymentLinks),
      ]),
      filter(([, isBuyerOriginApp, isPaymentLinks]) => {
        // Only load buyer accounts for buyer origin apps AND not payment links flow
        return isBuyerOriginApp && !isPaymentLinks;
      }),
      exhaustMap(() =>
        of(BtPayPaymentInformationActions.GetBuyerAccountsAction()),
      ),
    ),
  );

  /** istanbul ignore next */
  loadUntilPaymentPreviewComplete$ = createEffect(
    () =>
      this.loadUntilActionsComplete(
        [SessionActions.GetSessionSummarySuccessAction.type],
        [
          PayOnAccountCoreComponentActions
            .createPayOnAccountPaymentPreviewCompleteAction.type,
          PayOnAccountCoreComponentActions
            .createPayOnAccountPaymentPreviewErrorAction.type,
          PayOnAccountCoreComponentActions.skipPayOnAccountPaymentPreviewAction
            .type,
        ],
      ),
    { dispatch: false },
  );
}
